import React, { useState, useEffect, useCallback } from 'react';
import { 
  Package, 
  Search, 
  Plus, 
  Edit2, 
  Trash2,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  History,
  Settings,
  Tag
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { staffService } from '../../services/staffService';
import { toast } from 'sonner';

const UNITS = ['lb', 'oz', 'kg', 'g', 'gal', 'qt', 'l', 'ml', 'each', 'dozen', 'case'];

const StaffInventory = () => {
  const [inventory, setInventory] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [categoriesDialogOpen, setCategoriesDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [inventoryData, categoriesData] = await Promise.all([
        staffService.getInventory(),
        staffService.getCategories()
      ]);
      setInventory(inventoryData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadInventory = useCallback(async () => {
    try {
      const filters = {};
      if (categoryFilter !== 'all') filters.category = categoryFilter;
      if (lowStockOnly) filters.low_stock_only = true;
      const data = await staffService.getInventory(filters);
      setInventory(data);
    } catch (error) {
      console.error('Error loading inventory:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, lowStockOnly]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  const handleCreateIngredient = async (data) => {
    try {
      await staffService.createIngredient(data);
      toast.success('Ingredient added');
      loadInventory();
      setCreateDialogOpen(false);
    } catch (error) {
      toast.error('Failed to add ingredient');
    }
  };

  const handleUpdateIngredient = async (data) => {
    try {
      await staffService.updateIngredient(selectedItem.id, data);
      toast.success('Ingredient updated');
      loadInventory();
      setEditDialogOpen(false);
      setSelectedItem(null);
    } catch (error) {
      toast.error('Failed to update ingredient');
    }
  };

  const handleDeleteIngredient = async (id) => {
    if (!window.confirm('Are you sure you want to delete this ingredient?')) return;
    try {
      await staffService.deleteIngredient(id);
      toast.success('Ingredient deleted');
      loadInventory();
    } catch (error) {
      toast.error('Failed to delete ingredient');
    }
  };

  const handleAdjustInventory = async (data) => {
    try {
      const result = await staffService.adjustInventory(data);
      toast.success(result.message);
      loadInventory();
      setAdjustDialogOpen(false);
      setSelectedItem(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust inventory');
    }
  };

  const handleCreateCategory = async (data) => {
    try {
      await staffService.createCategory(data);
      toast.success('Category created');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      await staffService.deleteCategory(id);
      toast.success('Category deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const filteredInventory = inventory.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const lowStockCount = inventory.filter(i => i.is_low_stock).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-inventory">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventory</h1>
          <p className="text-sm text-gray-600 mt-1">
            {inventory.length} items • {categories.length} categories • {lowStockCount > 0 && (
              <span className="text-red-600">{lowStockCount} low stock</span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Dialog open={categoriesDialogOpen} onOpenChange={setCategoriesDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Tag className="h-4 w-4 mr-2" />
                Categories
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Manage Categories</DialogTitle>
                <DialogDescription className="sr-only">Manage Categories dialog. Use the form below to continue.</DialogDescription>
              </DialogHeader>
              <CategoryManager
                categories={categories}
                onCreateCategory={handleCreateCategory}
                onDeleteCategory={handleDeleteCategory}
              />
            </DialogContent>
          </Dialog>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-orange-500 hover:bg-orange-600">
                <Plus className="h-4 w-4 mr-2" />
                Add Ingredient
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Add New Ingredient</DialogTitle>
                <DialogDescription className="sr-only">Add New Ingredient dialog. Use the form below to continue.</DialogDescription>
              </DialogHeader>
              <IngredientForm categories={categories} onSubmit={handleCreateIngredient} onCancel={() => setCreateDialogOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search ingredients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat.name} value={cat.name}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant={lowStockOnly ? "default" : "outline"}
          onClick={() => setLowStockOnly(!lowStockOnly)}
          className={lowStockOnly ? "bg-red-500 hover:bg-red-600" : ""}
        >
          <AlertTriangle className="h-4 w-4 mr-2" />
          Low Stock
        </Button>
      </div>

      {/* Inventory Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Ingredient</th>
                  <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase">In Stock</th>
                  <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase">Min Level</th>
                  <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredInventory.map((item) => (
                  <tr key={item.id} className={`hover:bg-gray-50 ${item.is_low_stock ? 'bg-red-50' : ''}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${item.is_low_stock ? 'bg-red-100' : 'bg-orange-100'}`}>
                          <Package className={`h-4 w-4 ${item.is_low_stock ? 'text-red-600' : 'text-orange-600'}`} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{item.name}</p>
                          {item.supplier && <p className="text-xs text-gray-500">{item.supplier}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge variant="outline" className="capitalize">{item.category}</Badge>
                    </td>
                    <td className="p-4 text-right">
                      <span className={`font-bold ${item.is_low_stock ? 'text-red-600' : 'text-gray-900'}`}>
                        {item.current_quantity}
                      </span>
                      <span className="text-gray-500 ml-1">{item.unit}</span>
                    </td>
                    <td className="p-4 text-right text-gray-600">
                      {item.min_quantity} {item.unit}
                    </td>
                    <td className="p-4">
                      {item.is_low_stock ? (
                        <Badge className="bg-red-100 text-red-700">Low Stock</Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700">OK</Badge>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex gap-1 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedItem(item);
                            setAdjustDialogOpen(true);
                          }}
                          title="Adjust Quantity"
                        >
                          <TrendingUp className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedItem(item);
                            setEditDialogOpen(true);
                          }}
                          title="Edit"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteIngredient(item.id)}
                          className="text-red-500 hover:text-red-700"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredInventory.length === 0 && (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No ingredients found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Ingredient</DialogTitle>
            <DialogDescription className="sr-only">Edit Ingredient dialog. Use the form below to continue.</DialogDescription>
          </DialogHeader>
          {selectedItem && (
            <IngredientForm
              ingredient={selectedItem}
              categories={categories}
              onSubmit={handleUpdateIngredient}
              onCancel={() => {
                setEditDialogOpen(false);
                setSelectedItem(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Adjust Dialog */}
      <Dialog open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adjust Inventory</DialogTitle>
            <DialogDescription className="sr-only">Adjust Inventory dialog. Use the form below to continue.</DialogDescription>
          </DialogHeader>
          {selectedItem && (
            <AdjustForm
              ingredient={selectedItem}
              onSubmit={handleAdjustInventory}
              onCancel={() => {
                setAdjustDialogOpen(false);
                setSelectedItem(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Category Manager Component
const CategoryManager = ({ categories, onCreateCategory, onDeleteCategory }) => {
  const [newCategory, setNewCategory] = useState({ name: '', label: '', description: '' });
  const [showForm, setShowForm] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newCategory.name || !newCategory.label) {
      return;
    }
    onCreateCategory({
      name: newCategory.name.toLowerCase().replace(/\s+/g, '_'),
      label: newCategory.label,
      description: newCategory.description
    });
    setNewCategory({ name: '', label: '', description: '' });
    setShowForm(false);
  };

  return (
    <div className="space-y-4">
      {/* Existing categories */}
      <div className="space-y-2 max-h-60 overflow-y-auto">
        {categories.map((cat) => (
          <div key={cat.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium">{cat.label}</p>
              <p className="text-xs text-gray-500">{cat.ingredient_count} ingredients</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (cat.ingredient_count > 0) {
                  alert(`Cannot delete: ${cat.ingredient_count} ingredients are using this category.`);
                } else if (window.confirm(`Delete category "${cat.label}"?`)) {
                  onDeleteCategory(cat.id);
                }
              }}
              className="text-red-500 hover:text-red-700"
              disabled={cat.ingredient_count > 0}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        {categories.length === 0 && (
          <p className="text-center text-gray-500 py-4">No categories yet</p>
        )}
      </div>

      {/* Add new category form */}
      {showForm ? (
        <form onSubmit={handleSubmit} className="space-y-3 p-4 border rounded-lg bg-orange-50">
          <div>
            <Label>Category Name (internal)</Label>
            <Input
              value={newCategory.name}
              onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
              placeholder="e.g., frozen"
              required
            />
          </div>
          <div>
            <Label>Display Label</Label>
            <Input
              value={newCategory.label}
              onChange={(e) => setNewCategory({ ...newCategory, label: e.target.value })}
              placeholder="e.g., Frozen Items"
              required
            />
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" className="flex-1 bg-orange-500 hover:bg-orange-600">
              Add Category
            </Button>
          </div>
        </form>
      ) : (
        <Button onClick={() => setShowForm(true)} variant="outline" className="w-full">
          <Plus className="h-4 w-4 mr-2" />
          Add New Category
        </Button>
      )}
    </div>
  );
};

// Ingredient Form Component
const IngredientForm = ({ ingredient, categories, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: ingredient?.name || '',
    category: ingredient?.category || (categories[0]?.name || 'other'),
    unit: ingredient?.unit || 'lb',
    current_quantity: ingredient?.current_quantity || 0,
    min_quantity: ingredient?.min_quantity || 0,
    cost_per_unit: ingredient?.cost_per_unit || 0,
    supplier: ingredient?.supplier || '',
    notes: ingredient?.notes || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      current_quantity: parseFloat(formData.current_quantity),
      min_quantity: parseFloat(formData.min_quantity),
      cost_per_unit: parseFloat(formData.cost_per_unit)
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>Ingredient Name</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Category</Label>
          <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categories.map(cat => (
                <SelectItem key={cat.name} value={cat.name}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Unit</Label>
          <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {UNITS.map(unit => (
                <SelectItem key={unit} value={unit}>{unit}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Current Quantity</Label>
          <Input
            type="number"
            step="0.01"
            value={formData.current_quantity}
            onChange={(e) => setFormData({ ...formData, current_quantity: e.target.value })}
          />
        </div>
        <div>
          <Label>Minimum Level</Label>
          <Input
            type="number"
            step="0.01"
            value={formData.min_quantity}
            onChange={(e) => setFormData({ ...formData, min_quantity: e.target.value })}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Cost per Unit ($)</Label>
          <Input
            type="number"
            step="0.01"
            value={formData.cost_per_unit}
            onChange={(e) => setFormData({ ...formData, cost_per_unit: e.target.value })}
          />
        </div>
        <div>
          <Label>Supplier</Label>
          <Input
            value={formData.supplier}
            onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
            placeholder="Optional"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" className="bg-orange-500 hover:bg-orange-600">
          {ingredient ? 'Update' : 'Add'} Ingredient
        </Button>
      </div>
    </form>
  );
};

// Adjust Form Component
const AdjustForm = ({ ingredient, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    ingredient_id: ingredient.id,
    quantity_change: '',
    reason: 'restock',
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      quantity_change: parseFloat(formData.quantity_change)
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 bg-slate-50 rounded-lg">
        <p className="font-medium">{ingredient.name}</p>
        <p className="text-sm text-gray-600">Current: {ingredient.current_quantity} {ingredient.unit}</p>
      </div>
      <div>
        <Label>Adjustment Amount</Label>
        <div className="flex gap-2 items-center">
          <Input
            type="number"
            step="0.01"
            value={formData.quantity_change}
            onChange={(e) => setFormData({ ...formData, quantity_change: e.target.value })}
            placeholder="e.g., 10 or -5"
            required
          />
          <span className="text-gray-600">{ingredient.unit}</span>
        </div>
        <p className="text-xs text-gray-500 mt-1">Use positive for adding, negative for removing</p>
      </div>
      <div>
        <Label>Reason</Label>
        <Select value={formData.reason} onValueChange={(v) => setFormData({ ...formData, reason: v })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="restock">Restock</SelectItem>
            <SelectItem value="production">Used in Production</SelectItem>
            <SelectItem value="waste">Waste/Spoilage</SelectItem>
            <SelectItem value="correction">Inventory Correction</SelectItem>
            <SelectItem value="count">Physical Count</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Notes (Optional)</Label>
        <Input
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Add notes..."
        />
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" className="bg-orange-500 hover:bg-orange-600">
          Adjust Inventory
        </Button>
      </div>
    </form>
  );
};

export default StaffInventory;
